import { useTranslation } from "react-i18next";
import { useState, useMemo, useContext, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";

import { UserContext } from "../../../hooks/UserContext";
import {
    PageTopBar,
    PageView,
    Button,
    Notification,
    ConfirmDialog,
    realCarrierName
} from "../../../components";
import { Loader } from "../../../components/Loader";
import {
    fetchDispatchSchedules,
    createDispatchSchedule,
    updateDispatchSchedule,
    deleteDispatchSchedule,
    fetchAvailableChannelTypes,
    fetchAvailableStates,
    fetchLastmileShippingMethods
} from "../../../services";
import { fetchWarehouse, fetchWarehouseShippingMethods } from "../../../services/warehouseSevices";
import { DispatchScheduleFormModal } from "./DispatchScheduleFormModal";
import { DispatchScheduleHistoryModal } from "./DispatchScheduleHistoryModal";
import { DispatchScheduleCalendarView } from "./DispatchScheduleCalendarView";
import { DispatchScheduleFilters } from "./DispatchScheduleFilters";
import { detectScheduleType } from "../utils";

const T_PREFIX = 'lastmile_management.dispatch_schedules';

const BATCH_SIZE = 5;

const batchedAllSettled = async (items, fn) => {
    const results = [];
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const batch = items.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.allSettled(batch.map(fn));
        results.push(...batchResults);
    }
    return results;
};

export const ListDispatchSchedulesContainer = () => {
    const { i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const { warehouse_id } = useParams();
    const queryClient = useQueryClient();

    // Permissions
    const hasAccess = !!user?.permissions?.index_dispatch_schedules;
    const canCreate = !!user?.permissions?.create_dispatch_schedules;
    // Also gates delete actions — backend uses the same permission for update and delete
    const canEdit = !!user?.permissions?.update_dispatch_schedules;

    // Filter state
    const [filters, setFilters] = useState({
        carrier_name: null,
        channel_type_id: null,
        shipping_method_id: null,
        shipping_state: null,
        enable: 'true'
    });

    // Build search params from filters
    const searchParams = useMemo(() => {
        const params = { warehouse_id };
        if (filters.carrier_name) params.carrier_name = filters.carrier_name;
        if (filters.channel_type_id) params.channel_type_id = filters.channel_type_id;
        if (filters.shipping_method_id) params.shipping_method_id = filters.shipping_method_id;
        if (filters.shipping_state && filters.shipping_state.length > 0) params.shipping_state = filters.shipping_state;
        if (filters.enable === 'true') params.enable = true;
        if (filters.enable === 'false') params.enable = false;
        return params;
    }, [filters, warehouse_id]);

    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleResetFilters = () => {
        setFilters({ carrier_name: null, channel_type_id: null, shipping_method_id: null, shipping_state: null, enable: null });
    };

    // Modal state
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [selectedHistorySchedule, setSelectedHistorySchedule] = useState(null);
    const [notification, setNotification] = useState({ show: false, type: '', title: '' });
    const notificationTimerRef = useRef(null);
    const [isMultiCreateLoading, setIsMultiCreateLoading] = useState(false);
    const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);
    const [scheduleToToggle, setScheduleToToggle] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [scheduleToDelete, setScheduleToDelete] = useState(null);
    const [createDefaults, setCreateDefaults] = useState(null);

    // Queries
    const { data: warehouse } = useQuery(
        ['warehouse', warehouse_id],
        () => fetchWarehouse(warehouse_id),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    );

    const fetchAllSchedules = useCallback(async () => {
        const perPage = 200;
        const maxPages = 10;
        const firstPage = await fetchDispatchSchedules({ ...searchParams, page: 1, per_page: perPage });
        const allItems = [...(firstPage.warehouse_dispatch_schedules || [])];
        const totalPages = firstPage.meta?.total_pages || 1;
        const pagesToFetch = Math.min(totalPages, maxPages);
        if (totalPages > maxPages) {
            console.warn(`Dispatch schedules truncated: ${totalPages} pages available, capped at ${maxPages}`);
        }
        if (pagesToFetch > 1) {
            const pages = Array.from({ length: pagesToFetch - 1 }, (_, i) => i + 2);
            const results = await Promise.allSettled(
                pages.map(p => fetchDispatchSchedules({ ...searchParams, page: p, per_page: perPage }))
            );
            results.forEach(r => {
                if (r.status === 'fulfilled') allItems.push(...(r.value.warehouse_dispatch_schedules || []));
            });
        }
        return { warehouse_dispatch_schedules: allItems };
    }, [searchParams]);

    const { isLoading, isError, error, data, isPreviousData } = useQuery(
        ['dispatch_schedules', searchParams],
        fetchAllSchedules,
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            enabled: hasAccess
        }
    );

    const { data: warehouseShippingMethodsData } = useQuery(
        ['warehouse_shipping_methods', warehouse_id],
        () => fetchWarehouseShippingMethods(warehouse_id),
        { keepPreviousData: true, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: !!warehouse_id }
    );

    const warehouseCarrierNames = useMemo(() => {
        const methods = Array.isArray(warehouseShippingMethodsData) ? warehouseShippingMethodsData : warehouseShippingMethodsData?.shipping_methods || [];
        // Use the REAL carrier so the dispatch-schedule carrier options (and the
        // carrier_name persisted on create/filter) stay "UBER", not the "Cubbo"
        // whitelabel — Uber is managed as its own carrier here (CUBBO-2185).
        return [...new Set(methods.map(sm => realCarrierName(sm.carrier_name, sm.internal_carrier_name)).filter(Boolean))];
    }, [warehouseShippingMethodsData]);

    const { data: channelTypesData } = useQuery(
        ['dispatch_schedule_available_channel_types', warehouse_id],
        () => fetchAvailableChannelTypes({ warehouse_id }),
        { keepPreviousData: true, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: !!warehouse_id }
    );

    const { data: availableStatesData } = useQuery(
        ['dispatch_schedule_available_states', warehouse_id],
        () => fetchAvailableStates({ warehouse_id }),
        { keepPreviousData: true, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: !!warehouse_id }
    );

    const { data: shippingMethodsData } = useQuery(
        ['lastmile_shipping_methods', warehouse_id],
        () => fetchLastmileShippingMethods({ warehouse_id }),
        { keepPreviousData: true, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: !!warehouse_id }
    );

    // Mutations
    const createMutation = useMutation(createDispatchSchedule, {
        onSuccess: () => {
            queryClient.invalidateQueries(['dispatch_schedules']);
            setIsFormModalOpen(false);
            showNotification('success', i18n.t(`${T_PREFIX}.notifications.create_success`));
        },
        onError: (err) => {
            showNotification('error', err?.response?.data?.error || i18n.t(`${T_PREFIX}.notifications.create_error`));
        }
    });

    const updateMutation = useMutation(
        ({ id, data: updateData }) => updateDispatchSchedule(id, updateData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['dispatch_schedules']);
                // Do NOT clear selectedSchedule here: the dialog stays mounted through
                // its leave transition, so dropping it mid-close re-renders the form in
                // "create" mode while its state still holds the edit-mode shape. Both
                // create entry points clear it before reopening.
                setIsFormModalOpen(false);
                showNotification('success', i18n.t(`${T_PREFIX}.notifications.update_success`));
            },
            onError: (err) => {
                showNotification('error', err?.response?.data?.error || i18n.t(`${T_PREFIX}.notifications.update_error`));
            }
        }
    );

    const moveMutation = useMutation(
        ({ id, data: moveData }) => updateDispatchSchedule(id, moveData)
    );

    const toggleEnableMutation = useMutation(
        ({ id, enable }) => updateDispatchSchedule(id, { enable }),
        {
            onError: (err) => {
                showNotification('error', err?.response?.data?.error || i18n.t(`${T_PREFIX}.notifications.update_error`));
            }
        }
    );

    const deleteMutation = useMutation(
        (id) => deleteDispatchSchedule(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['dispatch_schedules']);
                showNotification('success', i18n.t(`${T_PREFIX}.notifications.delete_success`));
            },
            onError: (err) => {
                showNotification('error', err?.response?.data?.error || i18n.t(`${T_PREFIX}.notifications.delete_error`));
            }
        }
    );

    // Handlers
    const showNotification = (type, title) => {
        if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
        setNotification({ show: true, type, title });
        notificationTimerRef.current = setTimeout(() => {
            setNotification(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    useEffect(() => {
        return () => {
            if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
        };
    }, []);

    const handleOpenCreateModal = () => {
        setSelectedSchedule(null);
        setCreateDefaults(null);
        setIsFormModalOpen(true);
    };

    const handleCreateFromDay = (day) => {
        setSelectedSchedule(null);
        setCreateDefaults({ dispatch_day: day });
        setIsFormModalOpen(true);
    };

    const handleConfirmToggle = () => {
        if (scheduleToToggle) {
            const wasEnabled = scheduleToToggle.enable;
            toggleEnableMutation.mutate(
                { id: scheduleToToggle.id, enable: !wasEnabled },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries(['dispatch_schedules']);
                        const willHide = (wasEnabled && filters.enable === 'true') || (!wasEnabled && filters.enable === 'false');
                        let msg = i18n.t(`${T_PREFIX}.notifications.update_success`);
                        if (willHide) {
                            msg += '. ' + i18n.t(`${T_PREFIX}.notifications.hidden_by_filter`);
                        }
                        showNotification('success', msg);
                    },
                    onSettled: () => {
                        setConfirmToggleOpen(false);
                        setScheduleToToggle(null);
                    }
                }
            );
        }
    };

    const handleSubmitForm = async (formData) => {
        if (selectedSchedule) {
            updateMutation.mutate({ id: selectedSchedule.id, data: formData });
        } else {
            const { dispatch_days, shipping_state, ...rest } = formData;
            const wId = parseInt(warehouse_id, 10);
            if (dispatch_days && dispatch_days.length > 0) {
                setIsMultiCreateLoading(true);
                const results = await batchedAllSettled(dispatch_days, day =>
                    createDispatchSchedule({ ...rest, shipping_state: shipping_state, dispatch_day: day, warehouse_id: wId })
                );
                const succeeded = results.filter(r => r.status === 'fulfilled').length;
                const failed = results.filter(r => r.status === 'rejected').length;
                queryClient.invalidateQueries(['dispatch_schedules']);
                if (failed === 0) {
                    setIsFormModalOpen(false);
                    showNotification('success', i18n.t(`${T_PREFIX}.notifications.create_success`));
                } else if (succeeded === 0) {
                    showNotification('error', i18n.t(`${T_PREFIX}.notifications.create_error`));
                } else {
                    setIsFormModalOpen(false);
                    showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_partial_error`, { succeeded, failed }));
                }
                setIsMultiCreateLoading(false);
            } else {
                createMutation.mutate({ ...rest, shipping_state: shipping_state, warehouse_id: wId });
            }
        }
    };

    const handleEditSchedule = (schedule) => {
        setSelectedSchedule(schedule);
        setIsFormModalOpen(true);
    };

    const handleViewHistory = (schedule) => {
        setSelectedHistorySchedule(schedule);
        setIsHistoryModalOpen(true);
    };

    const handleToggleEnable = (schedule) => {
        setScheduleToToggle(schedule);
        setConfirmToggleOpen(true);
    };

    const handleDuplicateSchedule = (schedule) => {
        setSelectedSchedule(null);
        setCreateDefaults({
            // Prefill the REAL carrier so it matches the carrier options (which
            // use internal_carrier_name) and persists correctly (CUBBO-2185).
            carrier_name: realCarrierName(schedule.carrier_name, schedule.internal_carrier_name),
            channel_type_id: schedule.channel_type_id,
            shipping_state: schedule.shipping_state,
            shipping_method_id: schedule.shipping_method_id,
            schedule_type: detectScheduleType(schedule),
            dispatch_time: schedule.dispatch_time,
            time_before_dispatch: schedule.time_before_dispatch
        });
        setIsFormModalOpen(true);
    };

    const handleDeleteSchedule = (schedule) => {
        setScheduleToDelete(schedule);
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = () => {
        if (scheduleToDelete) {
            deleteMutation.mutate(scheduleToDelete.id, {
                onSettled: () => {
                    setConfirmDeleteOpen(false);
                    setScheduleToDelete(null);
                }
            });
        }
    };

    // Bulk actions state
    const [confirmBulkEnableOpen, setConfirmBulkEnableOpen] = useState(false);
    const [confirmBulkDisableOpen, setConfirmBulkDisableOpen] = useState(false);
    const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
    const [bulkIds, setBulkIds] = useState([]);
    const [isBulkLoading, setIsBulkLoading] = useState(false);

    const allSchedules = data?.warehouse_dispatch_schedules || [];

    const handleMoveSchedule = (scheduleId, newDay, { onSuccess, onError } = {}) => {
        const schedule = allSchedules.find(s => s.id === scheduleId);
        if (!schedule || schedule.dispatch_day === newDay) return;
        moveMutation.mutate(
            { id: scheduleId, data: { dispatch_day: newDay } },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries(['dispatch_schedules']);
                    showNotification('success', i18n.t(`${T_PREFIX}.notifications.move_success`));
                    onSuccess?.(scheduleId);
                },
                onError: (err) => {
                    showNotification('error', err?.response?.data?.error || i18n.t(`${T_PREFIX}.notifications.move_error`));
                    onError?.(scheduleId);
                }
            }
        );
    };

    const handleBulkEnable = (ids) => {
        setBulkIds(ids);
        setConfirmBulkEnableOpen(true);
    };

    const getScheduleLabel = (schedule) => {
        return schedule.carrier_name || schedule.channel_type_name || schedule.shipping_method_name || `ID ${schedule.id}`;
    };

    const handleConfirmBulkEnable = async () => {
        const alreadyEnabled = bulkIds.filter(id => allSchedules.find(s => s.id === id)?.enable);
        const toEnable = bulkIds.filter(id => { const s = allSchedules.find(sc => sc.id === id); return s && !s.enable; });

        if (toEnable.length === 0) {
            const labels = alreadyEnabled.map(id => getScheduleLabel(allSchedules.find(s => s.id === id))).join(', ');
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_already_enabled`, { names: labels }));
            setConfirmBulkEnableOpen(false);
            setBulkIds([]);
            return;
        }

        setIsBulkLoading(true);
        const results = await batchedAllSettled(toEnable, id => updateDispatchSchedule(id, { enable: true }));
        const succeeded = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        queryClient.invalidateQueries(['dispatch_schedules']);
        if (failed === 0) {
            let msg = i18n.t(`${T_PREFIX}.notifications.bulk_enable_success`, { count: succeeded });
            if (alreadyEnabled.length > 0) {
                const labels = alreadyEnabled.map(id => getScheduleLabel(allSchedules.find(s => s.id === id))).join(', ');
                msg += '. ' + i18n.t(`${T_PREFIX}.notifications.bulk_already_enabled`, { names: labels });
            }
            showNotification('success', msg);
        } else if (succeeded === 0) {
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_enable_error`));
        } else {
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_partial_error`, { succeeded, failed }));
        }
        setIsBulkLoading(false);
        setConfirmBulkEnableOpen(false);
        setBulkIds([]);
    };

    const handleBulkDisable = (ids) => {
        setBulkIds(ids);
        setConfirmBulkDisableOpen(true);
    };

    const handleConfirmBulkDisable = async () => {
        const alreadyDisabled = bulkIds.filter(id => { const s = allSchedules.find(sc => sc.id === id); return s && !s.enable; });
        const toDisable = bulkIds.filter(id => allSchedules.find(s => s.id === id)?.enable);

        if (toDisable.length === 0) {
            const labels = alreadyDisabled.map(id => getScheduleLabel(allSchedules.find(s => s.id === id))).join(', ');
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_already_disabled`, { names: labels }));
            setConfirmBulkDisableOpen(false);
            setBulkIds([]);
            return;
        }

        setIsBulkLoading(true);
        const results = await batchedAllSettled(toDisable, id => updateDispatchSchedule(id, { enable: false }));
        const succeeded = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        queryClient.invalidateQueries(['dispatch_schedules']);
        if (failed === 0) {
            let msg = i18n.t(`${T_PREFIX}.notifications.bulk_disable_success`, { count: succeeded });
            if (alreadyDisabled.length > 0) {
                const labels = alreadyDisabled.map(id => getScheduleLabel(allSchedules.find(s => s.id === id))).join(', ');
                msg += '. ' + i18n.t(`${T_PREFIX}.notifications.bulk_already_disabled`, { names: labels });
            }
            showNotification('success', msg);
        } else if (succeeded === 0) {
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_disable_error`));
        } else {
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_partial_error`, { succeeded, failed }));
        }
        setIsBulkLoading(false);
        setConfirmBulkDisableOpen(false);
        setBulkIds([]);
    };

    const handleBulkDelete = (ids) => {
        setBulkIds(ids);
        setConfirmBulkDeleteOpen(true);
    };

    const handleConfirmBulkDelete = async () => {
        setIsBulkLoading(true);
        const results = await batchedAllSettled(bulkIds, id => deleteDispatchSchedule(id));
        const succeeded = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        queryClient.invalidateQueries(['dispatch_schedules']);
        if (failed === 0) {
            showNotification('success', i18n.t(`${T_PREFIX}.notifications.bulk_delete_success`, { count: succeeded }));
        } else if (succeeded === 0) {
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_delete_error`));
        } else {
            showNotification('error', i18n.t(`${T_PREFIX}.notifications.bulk_partial_error`, { succeeded, failed }));
        }
        setIsBulkLoading(false);
        setConfirmBulkDeleteOpen(false);
        setBulkIds([]);
    };

    if (!hasAccess) {
        return (
            <PageView>
                <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</p>
                </div>
            </PageView>
        );
    }

    return (
        <>
            <Notification
                show={notification.show}
                setShow={(val) => setNotification(prev => ({ ...prev, show: val }))}
                type={notification.type}
                title={notification.title}
            />

            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold">
                            {i18n.t(`${T_PREFIX}.title`)}
                        </div>
                        {canCreate && (
                            <Button
                                type="primary"
                                onClick={handleOpenCreateModal}
                                className="px-4"
                            >
                                {i18n.t(`${T_PREFIX}.add_schedule`)}
                            </Button>
                        )}
                    </PageTopBar>
                }
                childrenFullWidth={true}
                topMenuFullWidth={true}
            >
                <DispatchScheduleFilters
                    carrierNames={warehouseCarrierNames}
                    channelTypes={channelTypesData?.channel_types || []}
                    availableStates={availableStatesData?.states || []}
                    shippingMethods={shippingMethodsData?.shipping_methods || []}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onReset={handleResetFilters}
                />

                {(!isPreviousData && isLoading) ? (
                    <Loader show={true} />
                ) : isError ? (
                    <div className="text-center py-12">
                        <p className="text-red-500">
                            {error?.message || i18n.t(`${T_PREFIX}.errors.fetch_error`)}
                        </p>
                    </div>
                ) : (
                    <>
                        {allSchedules.length > 0 ? (
                            <DispatchScheduleCalendarView
                                schedules={allSchedules}
                                shippingMethods={shippingMethodsData?.shipping_methods || []}
                                warehouseTimeZone={warehouse?.time_zone}
                                canEdit={canEdit}
                                canCreate={canCreate}
                                onEditSchedule={handleEditSchedule}
                                onViewHistory={handleViewHistory}
                                onToggleEnable={handleToggleEnable}
                                onDeleteSchedule={canEdit ? handleDeleteSchedule : undefined}
                                onCreateFromDay={canCreate ? handleCreateFromDay : undefined}
                                onMoveSchedule={canEdit ? handleMoveSchedule : undefined}
                                onDuplicateSchedule={canCreate ? handleDuplicateSchedule : undefined}
                                onBulkEnable={canEdit ? handleBulkEnable : undefined}
                                onBulkDisable={canEdit ? handleBulkDisable : undefined}
                                onBulkDelete={canEdit ? handleBulkDelete : undefined}
                                isToggleLoading={toggleEnableMutation.isLoading}
                                isBulkLoading={isBulkLoading}
                            />
                        ) : (
                            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                                <p className="text-gray-500">{i18n.t(`${T_PREFIX}.empty_table`)}</p>
                            </div>
                        )}
                    </>
                )}
            </PageView>

            <DispatchScheduleFormModal
                open={isFormModalOpen}
                setOpen={setIsFormModalOpen}
                scheduleData={selectedSchedule}
                defaultValues={createDefaults}
                onSubmit={handleSubmitForm}
                isLoading={createMutation.isLoading || updateMutation.isLoading || isMultiCreateLoading}
                carrierOptions={warehouseCarrierNames}
                channelTypes={channelTypesData?.channel_types || []}
                availableStates={availableStatesData?.states || []}
                shippingMethods={shippingMethodsData?.shipping_methods || []}
                warehouseTimeZone={warehouse?.time_zone}
            />

            <DispatchScheduleHistoryModal
                open={isHistoryModalOpen}
                setOpen={setIsHistoryModalOpen}
                scheduleData={selectedHistorySchedule}
                warehouseTimeZone={warehouse?.time_zone}
            />

            <ConfirmDialog
                open={confirmToggleOpen}
                setOpen={setConfirmToggleOpen}
                title={scheduleToToggle?.enable
                    ? i18n.t(`${T_PREFIX}.actions.disable`)
                    : i18n.t(`${T_PREFIX}.actions.enable`)
                }
                description={scheduleToToggle?.enable
                    ? i18n.t(`${T_PREFIX}.actions.confirm_disable`)
                    : i18n.t(`${T_PREFIX}.actions.confirm_enable`)
                }
                confirmLabel={scheduleToToggle?.enable
                    ? i18n.t(`${T_PREFIX}.actions.disable`)
                    : i18n.t(`${T_PREFIX}.actions.enable`)
                }
                cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
                onConfirm={handleConfirmToggle}
                onCancel={() => {
                    setConfirmToggleOpen(false);
                    setScheduleToToggle(null);
                }}
                loading={toggleEnableMutation.isLoading}
                type={scheduleToToggle?.enable ? 'danger' : 'default'}
            />

            <ConfirmDialog
                open={confirmDeleteOpen}
                setOpen={setConfirmDeleteOpen}
                title={i18n.t(`${T_PREFIX}.actions.delete`)}
                description={i18n.t(`${T_PREFIX}.actions.confirm_delete`)}
                confirmLabel={i18n.t(`${T_PREFIX}.actions.delete`)}
                cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setConfirmDeleteOpen(false);
                    setScheduleToDelete(null);
                }}
                loading={deleteMutation.isLoading}
                type="danger"
            />

            <ConfirmDialog
                open={confirmBulkEnableOpen}
                setOpen={setConfirmBulkEnableOpen}
                title={i18n.t(`${T_PREFIX}.bulk.confirm_enable_title`)}
                description={i18n.t(`${T_PREFIX}.bulk.confirm_enable_description`, { count: bulkIds.length })}
                confirmLabel={i18n.t(`${T_PREFIX}.actions.enable`)}
                cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
                onConfirm={handleConfirmBulkEnable}
                onCancel={() => {
                    setConfirmBulkEnableOpen(false);
                    setBulkIds([]);
                }}
                loading={isBulkLoading}
            />

            <ConfirmDialog
                open={confirmBulkDisableOpen}
                setOpen={setConfirmBulkDisableOpen}
                title={i18n.t(`${T_PREFIX}.bulk.confirm_disable_title`)}
                description={i18n.t(`${T_PREFIX}.bulk.confirm_disable_description`, { count: bulkIds.length })}
                confirmLabel={i18n.t(`${T_PREFIX}.actions.disable`)}
                cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
                onConfirm={handleConfirmBulkDisable}
                onCancel={() => {
                    setConfirmBulkDisableOpen(false);
                    setBulkIds([]);
                }}
                loading={isBulkLoading}
                type="danger"
            />

            <ConfirmDialog
                open={confirmBulkDeleteOpen}
                setOpen={setConfirmBulkDeleteOpen}
                title={i18n.t(`${T_PREFIX}.bulk.confirm_delete_title`)}
                description={i18n.t(`${T_PREFIX}.bulk.confirm_delete_description`, { count: bulkIds.length })}
                confirmLabel={i18n.t(`${T_PREFIX}.actions.delete`)}
                cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
                onConfirm={handleConfirmBulkDelete}
                onCancel={() => {
                    setConfirmBulkDeleteOpen(false);
                    setBulkIds([]);
                }}
                loading={isBulkLoading}
                type="danger"
            />
        </>
    );
};
